import { Button, Card, Input, Space, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { APPLY_STATE } from "@/meta/logistics/ship";
import { SHIP_TYPE } from "@/meta/logistics/shipInfo";
import { useDetailBack } from "@/utils/hooks/useDetailBack";
import type { ShipCertificationRow } from "./ShipCertificationPage";

export interface ShipCertificationDetailPageProps {
    loadApplication?: (id: string) => Promise<ShipCertificationRow>;
    reviewApplication?: (id: number, state: 2 | 3, reviewRemark: string) => Promise<unknown>;
}

/** 复用 Vue 船舶认证详情 VO 接口。 */
export async function loadShipCertificationDetail(id: string): Promise<ShipCertificationRow> {
    const response = await shippingRequestHttp.service.get<ShipCertificationRow>(
        `/ship/apply/vo/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用 Vue 审核接口的 query 参数契约。 */
export async function reviewShipCertification(id: number, state: 2 | 3, reviewRemark: string) {
    const response = await shippingRequestHttp.service.put(
        `/ship/apply/review?id=${id}&state=${state}&reviewRemark=${encodeURIComponent(reviewRemark)}`
    );
    return response.data;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 将详情值包装为单一节点，兼容 zhihao-ui-react 的值渲染结构。 */
const detailValue = (value: string) => <>{value}</>;

/** 物流管理-船舶认证详情/审核，覆盖 Vue 两种隐藏路由的共用页面。 */
export function ShipCertificationDetailPage({
    loadApplication = loadShipCertificationDetail,
    reviewApplication = reviewShipCertification
}: ShipCertificationDetailPageProps) {
    const { id = "" } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const goBack = useDetailBack("/logistics/shipManage/shipCertification");
    const isApply = location.pathname.includes("/shipCertification/apply/");
    const [detail, setDetail] = useState<ShipCertificationRow | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [reviewState, setReviewState] = useState<2 | 3 | null>(null);
    const [reviewRemark, setReviewRemark] = useState("");
    const [reviewing, setReviewing] = useState(false);

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少认证申请编号");
            return;
        }
        let disposed = false;
        void loadApplication(id)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("认证详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadApplication]);

    const apply = detail?.shipApply;
    const ship = detail?.shipVo?.ship;
    const submitReview = async () => {
        if (!reviewState || !apply?.id) return;
        setReviewing(true);
        try {
            await reviewApplication(Number(apply.id), reviewState, reviewRemark.trim());
            message.success(reviewState === 3 ? "已通过" : "已拒绝");
            navigate(`/logistics/shipManage/shipCertification/info/${apply.id}`);
        } catch {
            setErrorMessage("认证审核失败");
        } finally {
            setReviewing(false);
            setReviewState(null);
        }
    };

    return (
        <section aria-label="ship-certification-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "创建时间", value: dateText(apply?.createdTime) }]}
                    stateDesc={APPLY_STATE.getIntroByCode(Number(apply?.state)) || DEFAULT_VALUE}
                    title={isApply ? "审核船舶认证" : "船舶认证详情"}
                />
            </Card>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                <Card>
                    <div className="mb-5 text-base font-medium">AIS信息</div>
                    <ZhGrid cols={4} gap={[30, 20]}>
                        <ZhBaseItem label="船名" value={detailValue(text(ship?.cnname || ship?.name))} />
                        <ZhBaseItem label="MMSI" value={detailValue(text(ship?.id || apply?.shipId))} />
                        <ZhBaseItem
                            label="类型"
                            value={detailValue(SHIP_TYPE.getIntroByCode(ship?.type) || DEFAULT_VALUE)}
                        />
                        <ZhBaseItem label="IMO" value={detailValue(text(ship?.imo))} />
                        <ZhBaseItem label="呼号" value={detailValue(text(ship?.callSign))} />
                        <ZhBaseItem
                            label="船长/宽"
                            value={detailValue(`${text(ship?.length)} / ${text(ship?.width)} 米`)}
                        />
                        <ZhBaseItem label="吃水" value={detailValue(`${text(ship?.draft)} 米`)} />
                    </ZhGrid>
                    <div className="mb-5 mt-6 text-base font-medium">账号信息</div>
                    <ZhGrid cols={4} gap={[30, 20]}>
                        <ZhBaseItem label="组织机构名" value={detailValue(text(detail.customer?.institutionName))} />
                        <ZhBaseItem label="个人实名" value={detailValue(text(detail.customer?.realName))} />
                        <ZhBaseItem label="用户ID" value={detailValue(text(detail.customer?.code))} />
                    </ZhGrid>
                    <div className="mb-5 mt-6 text-base font-medium">证明材料</div>
                    <ZhGrid cols={4} gap={[30, 20]}>
                        <ZhBaseItem label="身份证" value={detailValue(text(apply?.idCardFileId))} />
                        <ZhBaseItem label="船舶营业运输证" value={detailValue(text(apply?.shipLicenseFileId))} />
                    </ZhGrid>
                    {!isApply && Number(apply?.state) !== APPLY_STATE.PENDING_APPROVAL.code ? (
                        <>
                            <div className="mb-5 mt-6 text-base font-medium">审核信息</div>
                            <ZhGrid cols={4} gap={[30, 20]}>
                                <ZhBaseItem
                                    label="结果"
                                    value={detailValue(
                                        APPLY_STATE.getIntroByCode(Number(apply?.state)) || DEFAULT_VALUE
                                    )}
                                />
                                <ZhBaseItem label="审核人" value={detailValue(text(detail.user?.name))} />
                                <ZhBaseItem label="时间" value={detailValue(dateText(apply?.reviewTime))} />
                                <ZhBaseItem label="备注" value={detailValue(text(apply?.reviewRemark))} />
                            </ZhGrid>
                        </>
                    ) : null}
                    <Space className="mt-4">
                        {isApply ? (
                            <>
                                <Button type="primary" onClick={() => setReviewState(3)}>
                                    同意
                                </Button>
                                <Button onClick={() => setReviewState(2)}>驳回</Button>
                            </>
                        ) : Number(apply?.state) === APPLY_STATE.PENDING_APPROVAL.code ? (
                            <Button
                                type="primary"
                                onClick={() => navigate(`/logistics/shipManage/shipCertification/apply/${id}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        <Button onClick={goBack}>返回</Button>
                    </Space>
                </Card>
            ) : null}
            <ZhDialog
                open={reviewState !== null}
                title={reviewState === 3 ? "审核通过" : "审核驳回"}
                width={544}
                onCancel={() => {
                    setReviewState(null);
                    setReviewRemark("");
                }}
                footer={
                    <Space>
                        <Button
                            disabled={reviewing}
                            onClick={() => {
                                setReviewState(null);
                                setReviewRemark("");
                            }}
                        >
                            返回
                        </Button>
                        <Button loading={reviewing} type="primary" onClick={() => void submitReview()}>
                            确认审核
                        </Button>
                    </Space>
                }
            >
                <Input.TextArea
                    aria-label="审核备注"
                    placeholder="请输入审核备注"
                    value={reviewRemark}
                    onChange={event => setReviewRemark(event.target.value)}
                />
            </ZhDialog>
        </section>
    );
}

export default ShipCertificationDetailPage;
